"use client";

import clsx from "clsx";
import { useEffect } from "react";
import { useSession } from "next-auth/react";
import Link from "next/link";

import { DataStatus, type BlogArticle, type BlogCategory, type Keyword } from "@/types";
import { sanitizeAllowedHtml } from "@/lib/security/sanitize-html";
import {
	getPreferredMedia,
	getPreviewMedia,
	getThumbnail,
	canViewPremiumBlogArticle,
} from "@/lib/utils";

import { Separator } from "@/components/ui/separator";
import BlogCard from "@/app/blogs/[...iden]/components/BlogCard";
import KeywordLink from "@/app/blogs/[...iden]/components/KeywordLink";
import CategoryCard from "@/app/blogs/[...iden]/components/CategoryCard";
import BlogMediaCarousel from "@/app/blogs/components/BlogsMediaCarousel";
import FeedbackSection from "@/app/blogs/[...iden]/components/FeedbackSection";
import MediaCard from "@/components/cards/MediaCard";
import { Button } from "@/components/ui/button";
import { ga4ViewBlog } from "@/lib/analytics/ga4-ecommerce";

import styles from "./BlogDetails.module.css";
// NOTE: react-draft-wysiwyg CSS removed - only needed for blog editing in admin area
// If blog editing is needed here, import it in the admin blog editor component instead

type Props = {
	blog: BlogArticle;
	allCategories: BlogCategory[];
	allKeywords: Keyword[];
	recentBlogs: BlogArticle[];
};

/** Draft exports sometimes double-encode HTML as a JSON string; bad data must not crash the page. */
function getSanitizedBlogBodyHtml(htmlExport: string | null | undefined): string {
	if (htmlExport == null || htmlExport === "") {
		return "";
	}
	let raw = String(htmlExport);
	const trimmed = raw.trim();
	if (trimmed.startsWith('"')) {
		try {
			const parsed = JSON.parse(trimmed);
			raw = typeof parsed === "string" ? parsed : raw;
		} catch {
			// leave raw as-is
		}
	}
	try {
		return sanitizeAllowedHtml(raw, {
			allowedTags: [
				"p",
				"br",
				"strong",
				"em",
				"u",
				"h1",
				"h2",
				"h3",
				"h4",
				"h5",
				"h6",
				"ul",
				"ol",
				"li",
				"a",
				"img",
				"blockquote",
				"code",
				"pre",
				"span",
				"div",
			],
			allowedAttributes: ["href", "src", "alt", "title", "class", "id"],
			allowedSchemesByTag: {
				img: ["http", "https", "data"],
			},
		});
	} catch {
		return "";
	}
}

export default function BlogDetails({
	blog,
	allCategories,
	allKeywords,
	recentBlogs,
}: Props) {
	const { data: session } = useSession();
	const { title, keywords: kx, blogCategories, readingTime, isPremium } = blog;

	const createdAt = new Date(blog.createdAt);
	const categories = blogCategories
		.filter((x) => x.dataStatus === DataStatus.ACTIVE)
		.map((x) => x.name);
	const keywords = kx.filter((x) => x.dataStatus === DataStatus.ACTIVE);

	const isBlogPremiumButNotUser =
		isPremium &&
		!canViewPremiumBlogArticle({
			isPremium,
			planDuration: session?.user?.plan?.duration,
			role: session?.user?.role,
		});

	const thumbnail = getThumbnail(blog.thumbnail);
	const media = getPreviewMedia(blog.media, blog.thumbnail);

	useEffect(() => {
		ga4ViewBlog({ id: blog._id, title: blog.title });
	}, [blog._id, blog.title]);

	return (
		<section className="container grid gap-8 px-0 pb-8 lg:grid-cols-[1fr_26rem] lg:p-8">
			<div className="flex flex-col">
				{isBlogPremiumButNotUser ? (
					<MediaCard data={media} thumbnail={thumbnail} disablePlayback />
				) : (
					<BlogMediaCarousel blog={blog} />
				)}

				<div className="relative mt-6 px-6 text-left sm:px-8 lg:px-0 lg:text-start">
					<span className="text-xsm2 text-grey-foreground">{categories.join(" | ")}</span>
					<div
						className={clsx(
							"absolute right-4 top-0 mt-0.5 flex h-6 w-20 items-center justify-center rounded-full text-xxsm font-medium uppercase ",
							blog.isPremium ? "bg-black text-tertiary" : "bg-tertiary text-white",
						)}
					>
						{blog.isPremium ? "Premium" : "Free"}
					</div>
					<h1 className="pt-2 text-xl font-semibold">{title}</h1>
				</div>

				<div className="mt-6 flex flex-wrap gap-2 px-6 sm:px-8 lg:px-0">
					{keywords.map((x) => (
						<KeywordLink key={x._id} keyword={x} />
					))}
				</div>

				<div className="mt-2 flex h-8 items-center justify-between px-6 text-xsm2 text-grey-foreground sm:px-8 lg:px-0">
					<span>{readingTime}</span>
				</div>

				{isBlogPremiumButNotUser ? (
					<DisabledContentArea isAuthenticated={!!session} />
				) : (
					<div
						className={styles["text-area"]}
						dangerouslySetInnerHTML={{
							__html: getSanitizedBlogBodyHtml(blog.htmlExport),
						}}
					/>
				)}

			{!isBlogPremiumButNotUser && (
				<FeedbackSection blogId={blog._id} createdAt={createdAt} />
			)}
			</div>

			<div className="flex flex-col gap-y-8 px-6 sm:px-8 lg:px-0">
				<div className="rounded-lg lg:p-4 lg:shadow-custom">
					<h2 className="text-base font-medium">Recent Content</h2>
					<Separator className="mb-4 mt-2 border-secondary/20" />
					<div className="flex flex-col gap-4">
						{recentBlogs.map((x, i) => (
							<div key={x._id} className="contents">
								<BlogCard blog={x} />
								{i !== recentBlogs.length - 1 && <Separator />}
							</div>
						))}
					</div>
				</div>

				<div>
					<h2 className="text-base font-medium">Categories</h2>
					<Separator className="mb-4 mt-2" />
					<div className="grid grid-cols-2 gap-2">
						{allCategories.map((x) => (
							<CategoryCard key={x._id} category={x} />
						))}
					</div>
				</div>

				<div>
					<h2 className="text-base font-medium">Keywords</h2>
					<Separator className="mb-4 mt-2" />
					<div className="flex flex-wrap gap-2">
						{allKeywords.map((x) => (
							<KeywordLink key={x._id} keyword={x} />
						))}
					</div>
				</div>
			</div>
		</section>
	);
}

type DisabledContentAreaProps = {
	isAuthenticated: boolean;
};
function DisabledContentArea({ isAuthenticated }: DisabledContentAreaProps) {
	const title = isAuthenticated ? "Premium-only content" : "Sign up to read the full content";

	return (
		<div className="grid [grid-template-areas:'stack']">
			<div className="z-10 flex flex-col items-center justify-center [grid-area:stack]">
				<h2 className="text-xl font-medium">{title}</h2>
				{isAuthenticated && (
					<span className="mt-1 max-w-prose text-center text-sm leading-tight">
						This content is only available to premium users. <br />
						Upgrade today to instantly unlock premium-only content and other benefits
					</span>
				)}

				<div className="mt-4 inline-flex gap-4">
					{isAuthenticated ? (
						<Button asChild>
							<Link href="/subscription/monthly">See plans</Link>
						</Button>
					) : (
						<>
							<Button asChild>
								<Link href="/login">Login</Link>
							</Button>
							<span className="self-center text-sm">Or</span>
							<Button asChild variant="outline">
								<Link href="/signup">Sign up</Link>
							</Button>
						</>
					)}
				</div>
			</div>

			<div
				aria-hidden
				className={clsx(
					"max-h-96 min-h-96 overflow-hidden opacity-50 blur [grid-area:stack]",
					styles["text-area"],
				)}
			>
				<p>
					Lorem ipsum, dolor sit amet consectetur adipisicing elit. Dolorem reprehenderit
					repellendus sint veniam hic eos officia ullam perspiciatis veritatis. Architecto
					nam laudantium ut ipsam quibusdam sapiente magnam enim. Corporis, accusamus
					impedit pariatur nemo, esse dolores quisquam qui earum aliquam corrupti
					provident ducimus sint ullam! Vitae blanditiis dolore incidunt ducimus fugiat
					quo suscipit, quaerat consequatur eligendi, necessitatibus obcaecati. Esse nam
					ipsam velit tempora amet dolorem commodi dolorum natus tempore facilis, eveniet
					explicabo possimus voluptas adipisci accusantium?
				</p>
				<p>
					Lorem ipsum dolor, sit amet consectetur adipisicing elit. Recusandae perferendis
					quibusdam sit nulla officia doloribus asperiores tempora, facilis corrupti
					magnam! Accusamus neque quas amet quaerat?
				</p>
				<p>
					Lorem ipsum dolor sit amet consectetur adipisicing elit. Incidunt, nesciunt,
					tempore voluptatum accusamus sit magni quod eaque consequuntur accusantium sed
					sunt saepe corporis iure possimus et architecto, explicabo amet earum facilis
					non harum vero voluptate illo exercitationem. Obcaecati aliquid, ipsam molestias
					molestiae quisquam placeat, tempore, iure repellendus id quasi aperiam ducimus.
					Obcaecati, maiores labore expedita eaque officia provident quibusdam quasi
					laborum autem corporis ipsam magnam, nobis odio nam quod dicta. Quisquam quidem
					expedita, quae quo mollitia dicta voluptatibus ab id officiis accusamus
					reiciendis laudantium odio voluptatum earum maxime natus numquam veniam quaerat
					quia laborum distinctio debitis. Incidunt eaque facere ex!
				</p>
			</div>
		</div>
	);
}
